import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { Globe, ArrowLeft, ArrowRight } from 'lucide-react';
import useMediaQuery, { MD_BREAKPOINT } from '@/hooks/useMediaQuery';
import { AUTH_COLORS } from '@/constants/theme';
import { MODAL_COLORS } from '@/components/modal';
import { useAuth } from '../hooks/useAuth';
import { switchLanguage } from '@/utils/langTransition';

import { platform } from '@/utils/platform';
import { validateEnglishName, validateHebrewName, validatePhone } from '@/utils/nameValidation';
import { FloatingField } from '@/components/ui/FloatingField';

type Step = 'email' | 'otp' | 'details';

export default function RegisterPage() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === 'rtl';
  const {
    sendOtp, sendOtpLoading,
    verifyOtp, verifyOtpLoading,
    register, registerLoading,
  } = useAuth();
  const navigate = useNavigate();

  const isMobile = useMediaQuery(MD_BREAKPOINT);

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [verificationToken, setVerificationToken] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [firstNameHe, setFirstNameHe] = useState('');
  const [lastNameHe, setLastNameHe] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [resendCountdown, setResendCountdown] = useState(0);

  // OTP input refs for auto-focus
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Resend countdown timer
  useEffect(() => {
    if (resendCountdown <= 0) return;
    const timer = setTimeout(() => setResendCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCountdown]);

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await sendOtp(email);
      setResendCountdown(60);
      setStep('otp');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || t('register.sendOtpFailed'));
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const data = await verifyOtp({ email, code });
      setVerificationToken(data.verificationToken);
      setStep('details');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || t('register.verifyOtpFailed'));
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const firstNameEnErr = validateEnglishName(firstName);
    if (firstNameEnErr) { setError(t(firstNameEnErr)); return; }
    const lastNameEnErr = validateEnglishName(lastName);
    if (lastNameEnErr) { setError(t(lastNameEnErr)); return; }
    const firstNameHeErr = validateHebrewName(firstNameHe);
    if (firstNameHeErr) { setError(t(firstNameHeErr)); return; }
    const lastNameHeErr = validateHebrewName(lastNameHe);
    if (lastNameHeErr) { setError(t(lastNameHeErr)); return; }
    const phoneErr = validatePhone(phone);
    if (phoneErr) { setError(t(phoneErr)); return; }

    if (password !== confirmPassword) {
      setError(t('register.passwordMismatch'));
      return;
    }
    try {
      await register({ email, password, firstName, lastName, firstNameHe, lastNameHe, phone, verificationToken, preferredLanguage: i18n.language });
      navigate(`/login?reason=pending_approval&name=${encodeURIComponent(firstName)}`);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || t('register.registerFailed'));
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const digits = code.split('');
    digits[index] = value.slice(-1);

    // Build the actual code string from what we have
    const built = Array(6).fill('').map((_, i) => digits[i] || '').join('');
    setCode(built.replace(/\s+$/, ''));

    // Auto-advance
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    setCode(pasted);
    // Focus the next empty slot or last
    const nextIndex = Math.min(pasted.length, 5);
    otpRefs.current[nextIndex]?.focus();
  };

  const glassInputStyle = {
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(255, 255, 255, 0.5)',
    color: '#0d2b6b',
  };

  const gradientButtonStyle = {
    background: 'linear-gradient(135deg, #1a4fa0 0%, #3b8dd4 100%)',
    color: '#ffffff',
    border: '1px solid rgba(255,255,255,0.2)',
    boxShadow: '0 4px 15px rgba(13,43,107,0.3)',
  };

  const BackArrow = isRtl ? ArrowRight : ArrowLeft;

  const langToggle = (
    <div className="flex justify-end" style={{ direction: 'ltr' }}>
      <button
        type="button"
        onClick={() => switchLanguage(i18n, i18n.language === 'he' ? 'en' : 'he', false)}
        className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-bold transition-all active:scale-95 cursor-pointer"
        style={{
          backgroundColor: 'rgba(255,255,255,0.5)',
          backdropFilter: 'blur(8px)',
          border: '1px solid rgba(255,255,255,0.4)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          color: '#0d2b6b',
        }}
      >
        <Globe size={14} />
        {i18n.language === 'he' ? 'English' : 'עברית'}
      </button>
    </div>
  );

  const formContent = (
    <>
      {langToggle}

      {/* Header */}
      <div className="text-center">
        <h1 className="text-2xl font-extrabold tracking-tight" style={{ color: '#0d2b6b' }}>{t('register.title')}</h1>
        <p className="mt-2 text-sm font-medium" style={{ color: '#1a4fa0', opacity: 0.7 }}>
          {step === 'email' && t('register.emailStep')}
          {step === 'otp' && t('register.otpStep')}
          {step === 'details' && t('register.detailsStep')}
        </p>
      </div>

      {/* Step indicator */}
      <div className="flex items-center justify-center gap-2">
        {(['email', 'otp', 'details'] as Step[]).map((s, i) => (
          <div
            key={s}
            className="h-1.5 rounded-full transition-all"
            style={{
              width: step === s ? 32 : 12,
              backgroundColor: step === s ? '#1a4fa0'
                : (['email', 'otp', 'details'].indexOf(step) > i ? '#1a4fa0' : 'rgba(13,43,107,0.15)'),
              opacity: step === s ? 1 : (['email', 'otp', 'details'].indexOf(step) > i ? 0.5 : 0.3),
            }}
          />
        ))}
      </div>

      {/* Error */}
      {error && (
        <div
          className="rounded-xl p-3 text-sm font-bold"
          style={{
            backgroundColor: AUTH_COLORS.errorBg,
            border: `1px solid ${MODAL_COLORS.border}`,
            color: AUTH_COLORS.errorText,
          }}
        >
          {error}
        </div>
      )}

      {/* Step 1: Email */}
      {step === 'email' && (
        <form onSubmit={handleSendOtp} className="space-y-4" autoComplete="off">
          <FloatingField
            label={t('auth.email')}
            inputId="reg-email"
            type="text"
            inputMode="email"
            required
            autoComplete="off"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            inputClassName="focus:ring-2 focus:ring-blue-400/50"
            inputStyle={glassInputStyle}
          />
          <button
            type="submit"
            disabled={sendOtpLoading}
            className="w-full rounded-xl py-3 text-sm font-bold transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
            style={gradientButtonStyle}
          >
            {sendOtpLoading ? t('common.loading') : t('register.sendCode')}
          </button>
        </form>
      )}

      {/* Step 2: OTP */}
      {step === 'otp' && (
        <form onSubmit={handleVerifyOtp} className="space-y-4" autoComplete="off">
          <p className="text-center text-sm" style={{ color: '#1a4fa0', opacity: 0.7 }}>
            {t('register.otpSentTo', { email })}
          </p>
          <div className="flex justify-center gap-2" dir="ltr">
            {Array.from({ length: 6 }).map((_, i) => (
              <input
                key={i}
                ref={(el) => { otpRefs.current[i] = el; }}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={1}
                value={code[i] || ''}
                onChange={(e) => handleOtpChange(i, e.target.value)}
                onKeyDown={(e) => handleOtpKeyDown(i, e)}
                onPaste={i === 0 ? handleOtpPaste : undefined}
                className="w-11 h-13 rounded-xl text-center text-xl font-bold outline-none focus:ring-2 focus:ring-blue-400/50 transition-all"
                style={glassInputStyle}
              />
            ))}
          </div>
          <button
            type="submit"
            disabled={verifyOtpLoading || code.replace(/\s/g, '').length < 6}
            className="w-full rounded-xl py-3 text-sm font-bold transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
            style={gradientButtonStyle}
          >
            {verifyOtpLoading ? t('common.loading') : t('register.verifyCode')}
          </button>
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => { setStep('email'); setCode(''); setError(null); }}
              className="text-sm font-bold cursor-pointer"
              style={{ color: '#1a4fa0' }}
            >
              <BackArrow size={14} className="inline mb-0.5" /> {t('register.changeEmail')}
            </button>
            <button
              type="button"
              disabled={resendCountdown > 0 || sendOtpLoading}
              onClick={async () => {
                setError(null);
                try {
                  await sendOtp(email);
                  setResendCountdown(60);
                  setCode('');
                } catch (err: unknown) {
                  const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
                  setError(msg || t('register.sendOtpFailed'));
                }
              }}
              className="text-sm font-bold cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ color: '#1a4fa0' }}
            >
              {resendCountdown > 0
                ? t('register.resendIn', { seconds: resendCountdown })
                : t('register.resendCode')}
            </button>
          </div>
        </form>
      )}

      {/* Step 3: Details */}
      {step === 'details' && (
        <form onSubmit={handleRegister} className="space-y-4" autoComplete="off">
          {/* Name in English */}
          <p className="text-xs font-bold uppercase tracking-wider" style={{ color: '#1a4fa0', opacity: 0.5 }}>
            {t('auth.nameInEnglish')}
          </p>
          <div className="grid grid-cols-2 gap-3" dir="ltr">
            <FloatingField
              label={t('auth.firstNameEn')}
              inputId="reg-firstName"
              name="reg-firstName"
              type="text"
              required
              autoComplete="off"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              inputClassName="focus:ring-2 focus:ring-blue-400/50"
              inputStyle={glassInputStyle}
            />
            <FloatingField
              label={t('auth.lastNameEn')}
              inputId="reg-lastName"
              name="reg-lastName"
              type="text"
              required
              autoComplete="off"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              inputClassName="focus:ring-2 focus:ring-blue-400/50"
              inputStyle={glassInputStyle}
            />
          </div>

          {/* Name in Hebrew */}
          <p className="text-xs font-bold uppercase tracking-wider" style={{ color: '#1a4fa0', opacity: 0.5 }}>
            {t('auth.nameInHebrew')}
          </p>
          <div className="grid grid-cols-2 gap-3">
            <FloatingField
              label={t('auth.firstNameHe')}
              inputId="reg-firstNameHe"
              name="reg-firstNameHe"
              type="text"
              required
              autoComplete="off"
              value={firstNameHe}
              onChange={(e) => setFirstNameHe(e.target.value)}
              inputDir="rtl"
              inputClassName="focus:ring-2 focus:ring-blue-400/50"
              inputStyle={glassInputStyle}
            />
            <FloatingField
              label={t('auth.lastNameHe')}
              inputId="reg-lastNameHe"
              name="reg-lastNameHe"
              type="text"
              required
              autoComplete="off"
              value={lastNameHe}
              onChange={(e) => setLastNameHe(e.target.value)}
              inputDir="rtl"
              inputClassName="focus:ring-2 focus:ring-blue-400/50"
              inputStyle={glassInputStyle}
            />
          </div>

          {/* Phone */}
          <FloatingField
            label={t('register.phone')}
            inputId="reg-phone"
            name="reg-phone"
            type="tel"
            required
            autoComplete="off"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            inputClassName="focus:ring-2 focus:ring-blue-400/50"
            inputStyle={glassInputStyle}
          />
          <FloatingField
            label={t('auth.password')}
            inputId="reg-password"
            name="reg-password"
            type="password"
            required
            minLength={8}
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            inputClassName="focus:ring-2 focus:ring-blue-400/50"
            inputStyle={glassInputStyle}
          />
          <FloatingField
            label={t('register.confirmPassword')}
            inputId="reg-confirmPassword"
            name="reg-confirmPassword"
            type="password"
            required
            minLength={8}
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            inputClassName="focus:ring-2 focus:ring-blue-400/50"
            inputStyle={glassInputStyle}
          />
          <button
            type="submit"
            disabled={registerLoading}
            className="w-full rounded-xl py-3 text-sm font-bold transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
            style={gradientButtonStyle}
          >
            {registerLoading ? t('common.loading') : t('register.submit')}
          </button>
        </form>
      )}

      {/* Back to Login */}
      <p className="text-center text-sm" style={{ color: '#1a4fa0', opacity: 0.7 }}>
        {t('register.hasAccount')}{' '}
        <Link to="/login" className="font-bold" style={{ color: '#0d2b6b' }}>
          {t('auth.login')}
        </Link>
      </p>
    </>
  );

  if (isMobile) {
    return (
      <div className="login-mesh-bg fixed inset-0" style={{ position: 'fixed' }}>
        <div
          className="relative z-10 h-full overflow-y-auto flex flex-col justify-center px-5 py-8 space-y-6"
          style={{
            paddingTop: platform.isAndroid ? '2rem' : 'calc(env(safe-area-inset-top, 0px) + 2rem)',
            backgroundColor: 'rgba(255,255,255,0.55)',
            backdropFilter: 'blur(20px) saturate(180%)',
          }}
        >
          {formContent}
        </div>
      </div>
    );
  }

  return (
    <div className="login-mesh-bg fixed inset-0 flex items-center justify-center" style={{ position: 'fixed' }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className="relative z-10 w-full max-w-md"
      >
        <div
          className="w-full space-y-6 rounded-3xl p-8"
          style={{
            backgroundColor: 'rgba(255,255,255,0.65)',
            backdropFilter: 'blur(20px) saturate(180%)',
            border: '1px solid rgba(255,255,255,0.4)',
            boxShadow: '0 8px 32px rgba(13,43,107,0.15), inset 0 1px 0 rgba(255,255,255,0.5)',
          }}
        >
          {formContent}
        </div>
      </motion.div>
    </div>
  );
}
